import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const navLinks = [
  { path: '/', label: 'nav.inicio' },
  { path: '/programas', label: 'nav.programas' },
  { path: '/audiolibros', label: 'nav.audiolibros' },
  { path: '/quiero-cambiar', label: 'nav.gratis' },
  { path: '/test-sueno', label: 'nav.testGratis' },
  { path: '/conoce-a-mercedes', label: 'mercedes.tag' },
  { path: '/#mas-alla-maternidad', label: 'mas_alla.section_subtitle' },
];

export default function Navbar({ overlayTone = 'light' }: { overlayTone?: 'light' | 'dark' }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // Text color while the navbar floats over a hero (not scrolled)
  const topText = overlayTone === 'dark' ? 'text-[#3D2B1F]' : 'text-white';
  const topTextMuted = overlayTone === 'dark' ? 'text-[#5C4A3A]/60' : 'text-white/60';

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const toggleLang = () => {
    const nextLang = i18n.language === 'es' ? 'en' : 'es';
    i18n.changeLanguage(nextLang);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="w-full px-6 md:px-10 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <span className={`font-serif text-2xl md:text-3xl font-semibold tracking-tight transition-colors duration-300 ${isScrolled ? 'text-[#8B6F5C]' : topText}`}>
            NeuroBabys
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            link.path ? (
              <Link
                key={link.path}
                to={link.path}
                className={`font-sans text-sm font-medium tracking-wide transition-colors duration-300 whitespace-nowrap ${
                  location.pathname === link.path
                    ? isScrolled ? 'text-[#8B6F5C] font-bold' : `${topText} font-bold`
                    : isScrolled ? 'text-[#5C4A3A] hover:text-[#8B6F5C]' : `${topText} hover:opacity-80`
                }`}
              >
                {t(link.label)}
              </Link>
            ) : (
              <span
                key={link.label}
                className={`font-sans text-sm font-medium tracking-wide transition-colors duration-300 whitespace-nowrap cursor-default ${
                  isScrolled ? 'text-[#5C4A3A]/50' : topTextMuted
                }`}
              >
                {t(link.label)}
              </span>
            )
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={toggleLang}
            className={`font-sans text-xs font-bold px-2 py-1 rounded transition-colors duration-300 whitespace-nowrap ${
              isScrolled
                ? 'text-[#5C4A3A] hover:text-[#8B6F5C]'
                : `${topText} hover:opacity-80`
            }`}
            title={i18n.language === 'es' ? 'Switch to English' : 'Cambiar a Español'}
          >
            {i18n.language === 'es' ? 'EN' : 'ES'}
          </button>
        </div>

        <div className="flex md:hidden items-center gap-3">
          <button
            onClick={toggleLang}
            className={`font-sans text-xs font-bold px-2 py-1 rounded transition-colors duration-300 ${
              isScrolled ? 'text-[#5C4A3A]' : topText
            }`}
          >
            {i18n.language === 'es' ? 'EN' : 'ES'}
          </button>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`w-10 h-10 flex items-center justify-center ${isScrolled ? 'text-[#3D2B1F]' : topText}`}
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-md border-t border-sand/30 px-6 py-6">
          <div className="flex flex-col gap-4">
            {navLinks.map((link) => (
              link.path ? (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-sans text-base font-medium transition-colors ${
                    location.pathname === link.path
                      ? 'text-[#8B6F5C] font-bold'
                      : 'text-[#5C4A3A]'
                  }`}
                >
                  {t(link.label)}
                </Link>
              ) : (
                <span
                  key={link.label}
                  className="font-sans text-base font-medium text-[#5C4A3A]/50 cursor-default"
                >
                  {t(link.label)}
                </span>
              )
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}